import { ColumnDef, Row, Cell } from "@tanstack/react-table";
import { EditableSelectOption } from "./EditableSelectCell";
import { SortFieldType } from "./sortUtils";

export interface BuildEditableSelectCellColumnProps<T> {
  accessorKey: string;
  header: string;
  options: EditableSelectOption[];
  onEdit: (value: string, row: T) => void;
  editable?: boolean | ((row: T) => boolean);
  size?: number;
  minSize?: number;
  enableSorting?: boolean;
  className?: string;
  sortType?: SortFieldType;
  meta?: Record<string, any>;
}

/**
 * Helper to create an inline-editable single-select column.
 *
 * Displays the selected option's label in the cell and opens a dropdown
 * on click so the user can pick a different value.
 *
 * @example
 * ```tsx
 * buildEditableSelectCellColumn<StudentRow>({
 *   accessorKey: "status",
 *   header: "Status",
 *   options: [
 *     { value: "active", label: "Active" },
 *     { value: "inactive", label: "Inactive" },
 *     { value: "pending", label: "Pending" }
 *   ],
 *   onEdit: (value, row) => handleStatusUpdate(row.id, value),
 *   editable: (row) => !row.isLocked,
 *   size: 140
 * })
 * ```
 */
export function buildEditableSelectCellColumn<T>({
  accessorKey,
  header,
  options,
  onEdit,
  editable = true,
  size,
  minSize,
  enableSorting,
  className,
  sortType,
  meta
}: BuildEditableSelectCellColumnProps<T>): ColumnDef<T> {
  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      editable:
        typeof editable === "function" ? (row: Row<T>) => (editable as (row: T) => boolean)(row.original) : editable,
      onEdit: (value: string, cell: Cell<T, unknown>) => onEdit(value, cell.row.original),
      editableInputType: "select" as const,
      editableSelectOptions: options,
      ...(className && { className }),
      ...(sortType && { sortType }),
      ...meta
    }
  };
}
